// Screens an automatic reload must not drop out from under (#308 review).
//
// `useVersionWatch` reloads a terminal that nobody is touching. That is the right default
// and wrong in two places, both of which are node-ui's business rather than the shared
// hook's — hence this file, so no route string lives in `@verlo/shared`.
//
// **An open dialog.** Only the unsent cart is in localStorage (`orderDraft`, and
// `splitBill` for split progress). `PaymentDialog` holds its stage, the cash tendered, the
// tip choice, the change it just worked out and the receipt address in component state, and
// twenty seconds — a minute, now — without a tap is ordinary there: a server counting change
// out of the drawer, a guest finding their card. Reloading then takes away the change-due
// figure they are reading.
//
// **The kitchen and bar boards.** These are the screens the reload rule was most confident
// about — a board on the wall goes untouched for minutes by design, which is why
// `useIdleLock` holds there — so they would reload on every release, and that is exactly
// where it does damage. `KitchenDisplayPage.playNewOrderSound` builds its `AudioContext`
// lazily and calls `ctx.resume()`; after an automatic reload the document has no user
// activation, so under Chrome's autoplay policy the context stays suspended, the oscillator
// makes no sound, and nothing throws — the new-ticket chime is simply gone until somebody
// happens to touch the board. The chime is how a chef knows there is a ticket, so losing it
// silently mid-service is worse than running a release behind. It would also reset a board
// a chef had muted back to chiming.
//
// A kiosk browser launched with `--autoplay-policy=no-user-gesture-required` would be exempt,
// but that flag lives in the hardware repo's firstboot, not here, so this does not assume it.
// These boards get the toast instead: staff bump tickets, so it is seen and tapped.

/** Boards whose new-ticket chime cannot survive a reload with no user gesture behind it. */
export const SOUND_BOARDS = ['/kitchen', '/bar-board'];

/** Is this one of them? */
export const isSoundBoard = (pathname) => SOUND_BOARDS.some((p) => (pathname || '').startsWith(p));

/**
 * Is there something on screen a reload would take away? Pure in its inputs so the rule is
 * tested without a browser.
 *
 * `role="alertdialog"` as well as `role="dialog"`: the refund and void confirmations are
 * `AlertDialog`, and those are the last thing to drop on someone.
 */
export const hasOpenDialog = (doc) =>
  !!doc?.querySelector?.('[role="dialog"], [role="alertdialog"]');

/** What `useVersionWatch` asks before reloading on its own. */
export const tillIsBusy = (doc = globalThis.document, loc = globalThis.location) =>
  isSoundBoard(loc?.pathname) || hasOpenDialog(doc);

export default tillIsBusy;
